import {
  AvatarPlaceholder,
  DocumentDownloadIcon,
  EditIcon,
  TrashAltIcon,
  VerticalDotsIcon
} from "assets/icons";
import {
  getBodyForChartRequest,
  useValidActiveChartRangeType
} from "components";
import { StatisticsChart } from "modules/dashboard";
import moment from "moment";
import { ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Avatar, Stat, TableItemActionsControl } from "ui";

import {
  CompanyCommunicationLog,
  CompanyLegalDocument,
  CompanyPeriodParams,
  DriverPerformanceRow,
  CompanySlaStatus,
  StatCardData,
  useDeleteCompanyDoc,
  useDeleteCompanyLog,
  useGetCompanyContact,
  useGetCompanySla,
  useGetDashboardMetrics,
  useGetDriverPerformance,
  useGetRevenue,
  useListCompanyDocs,
  useListCompanyLogs
} from "../../../apis";
import {
  HourglassIcon,
  KpiCheckIcon,
  KpiClockIcon,
  KpiCrossIcon,
  KpiStarIcon,
  TrendDownIcon,
  TrendUpIcon,
  TruckIcon
} from "../../../components/icons";
import {
  CommunicationLogModal,
  CompanySlaModal,
  LegalDocumentModal,
  OperationalContactModal
} from "../../../components/modals";

import styles from "./CompanyMetricsTab.module.scss";

const formatCount = (n: number): string => {
  if (n >= 1_000_000_000) return (n / 1_000_000_000).toFixed(1) + "B";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 10_000) return n.toLocaleString("en-US");
  return n.toLocaleString("en-US");
};

const formatToday = (n: number): string => n.toLocaleString("en-US");

const SLA_STATUS_LABEL: Record<CompanySlaStatus, string> = {
  Active: "Active",
  Inactive: "Inactive",
  Pending: "Pending"
};

const SLA_STATUS_CLASS: Record<CompanySlaStatus, string> = {
  Active: "slaBadgeActive",
  Inactive: "slaBadgeInactive",
  Pending: "slaBadgePending"
};

const formatRange = (from: string | null, to: string | null): string => {
  if (!from && !to) return "—";
  const fmt = (s: string | null) => (s ? moment(s).format("MMM D, YYYY") : "—");
  return `${fmt(from)} — ${fmt(to)}`;
};

const STATUS_LABEL: Record<DriverPerformanceRow["statusKey"], string> = {
  inTransit: "In transit",
  onShift: "On shift",
  offShift: "Off shift"
};

const STATUS_CLASS: Record<DriverPerformanceRow["statusKey"], string> = {
  inTransit: "statusBlue",
  onShift: "statusGreen",
  offShift: "statusGray"
};

const PhoneIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="4"
      y="1.5"
      width="8"
      height="13"
      rx="1.5"
      stroke="currentColor"
      strokeWidth="1.4"
    />
    <line
      x1="7"
      y1="12.3"
      x2="9"
      y2="12.3"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
    />
  </svg>
);

const UserIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8 8a2.67 2.67 0 1 0 0-5.34A2.67 2.67 0 0 0 8 8Zm0 1.33c-2.4 0-5.33 1.21-5.33 3.6V14h10.66v-1.07c0-2.4-2.93-3.6-5.33-3.6Z"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

const PlusIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7 1.75v10.5M1.75 7h10.5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const formatExpiry = (
  expiresAt: string | null
): { label: string; sub: string; warn: boolean } => {
  if (!expiresAt) return { label: "—", sub: "Valid until", warn: false };
  const m = moment(expiresAt);
  const days = m.diff(moment(), "days");
  if (days < 0)
    return { label: m.format("MMM YYYY"), sub: "Expired", warn: true };
  if (days <= 30)
    return { label: `in ${days} days`, sub: "Expires", warn: true };
  return { label: m.format("MMM YYYY"), sub: "Valid until", warn: false };
};

const TrendBadge = ({ value, label }: { value: number; label?: string }) => {
  const up = value >= 0;
  return (
    <span
      className={styles.trend + " " + (up ? styles.trendUp : styles.trendDown)}
    >
      {up ? <TrendUpIcon /> : <TrendDownIcon />}
      <span>
        {up ? "+" : "-"}
        {Math.abs(value)}%{label ? ` ${label}` : ""}
      </span>
    </span>
  );
};

const InlineCellShell = ({
  children,
  className
}: {
  children: ReactNode;
  className?: string;
}) => (
  <div className={styles.inlineCell + (className ? " " + className : "")}>
    {children}
  </div>
);

interface CompanyMetricsTabProps {
  companyId: string;
  showContactDetails?: boolean;
}

const CompanyMetricsTab = ({
  companyId,
  showContactDetails = true
}: CompanyMetricsTabProps) => {
  const { t } = useTranslation();

  const [leaderboardTab, setLeaderboardTab] = useState<"top" | "under" | "all">(
    "top"
  );

  /**
   * Every figure below follows the period picked for the chart. Clearing it
   * ("All time") drops both bounds, which the API reads as no period at all.
   */
  const [isAllTime, setIsAllTime] = useState(false);

  const today = new Date();
  const startOfYear = new Date(new Date().getFullYear(), 0, 1);
  const [dateRange, setDateRange] = useState<[Date, Date]>([
    startOfYear,
    today
  ]);

  const [isSlaOpen, setIsSlaOpen] = useState(false);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [isLogOpen, setIsLogOpen] = useState(false);
  const [editingLog, setEditingLog] = useState<CompanyCommunicationLog | null>(
    null
  );
  const [openedLogIdx, setOpenedLogIdx] = useState<number | null>(null);
  const [isDocOpen, setIsDocOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState<CompanyLegalDocument | null>(
    null
  );
  const [openedDocIdx, setOpenedDocIdx] = useState<number | null>(null);

  const { sla } = useGetCompanySla(companyId, {
    enabled: !!companyId && showContactDetails
  });
  const { contact } = useGetCompanyContact(companyId, {
    enabled: !!companyId && showContactDetails
  });
  const { logs } = useListCompanyLogs(companyId, {
    enabled: !!companyId && showContactDetails
  });
  const { docs } = useListCompanyDocs(companyId, {
    enabled: !!companyId && showContactDetails
  });
  const metricsParams = useMemo<CompanyPeriodParams>(
    () =>
      isAllTime
        ? {}
        : {
            from: moment(dateRange[0]).startOf("day").toISOString(),
            to: moment(dateRange[1]).endOf("day").toISOString()
          },
    [isAllTime, dateRange]
  );

  const { driverPerformance } = useGetDriverPerformance(
    companyId,
    metricsParams
  );
  const { dashboardMetrics } = useGetDashboardMetrics(companyId, metricsParams);

  const trendLabel = isAllTime
    ? t("in the last 7 days")
    : t("vs previous period");

  const { activeRangeType } = useValidActiveChartRangeType();
  const revenueParams = getBodyForChartRequest(dateRange, activeRangeType) as {
    from: string;
    to: string;
    breakdownBy: "Day" | "Month";
  };
  const { revenue, isLoading: isRevenueLoading } = useGetRevenue(
    companyId,
    revenueParams
  );

  const dpStats = driverPerformance?.stats;
  const LEADERBOARD_ROWS: Record<
    "top" | "under" | "all",
    DriverPerformanceRow[]
  > = {
    top: driverPerformance?.topPerformers ?? [],
    under: driverPerformance?.underperforming ?? [],
    all: driverPerformance?.allDrivers ?? []
  };

  const leaderboardRows = LEADERBOARD_ROWS[leaderboardTab];
  const topCount = LEADERBOARD_ROWS.top.length;
  const underCount = LEADERBOARD_ROWS.under.length;
  const allCount = LEADERBOARD_ROWS.all.length;

  const fmtNumber = (n: number) =>
    Number.isInteger(n) ? String(n) : n.toFixed(1);

  const { mutate: deleteLog } = useDeleteCompanyLog(companyId);
  const { mutate: deleteDoc } = useDeleteCompanyDoc(companyId);

  return (
    <div className={styles.metrics}>
      {/* Total Revenue — Dashboard's StatisticsChart */}
      <StatisticsChart
        totalTitle={t("Total Revenue (FCFA)")}
        titleValue={revenue?.totalRevenue ?? 0}
        isLoading={isRevenueLoading}
        comparablePeriodTitle={t("Today")}
        comparablePeriodValue={revenue?.today.value ?? 0}
        customPercentageInComparablePeriodValue={revenue?.today.trendPct ?? 0}
        withComparativePeriod
        isLoadingForCompare={isRevenueLoading}
        dateRange={dateRange}
        setDateRange={setDateRange}
        chartData={revenue?.data ?? []}
      />

      {/* Everything below reports on the period picked for the chart above */}
      <div className={styles.periodBar}>
        <span className={styles.periodBar__label}>{t("Figures for")}</span>
        <button
          type="button"
          className={
            styles.periodBar__option +
            (isAllTime ? "" : " " + styles.periodBar__optionActive)
          }
          onClick={() => setIsAllTime(false)}
        >
          {formatRange(
            dateRange[0].toISOString(),
            dateRange[1].toISOString()
          )}
        </button>
        <button
          type="button"
          className={
            styles.periodBar__option +
            (isAllTime ? " " + styles.periodBar__optionActive : "")
          }
          onClick={() => setIsAllTime(true)}
        >
          {t("All time")}
        </button>
      </div>

      {/* 5 stat cards */}
      <div className={styles.card}>
        <div className={styles.inlineRow + " " + styles.inlineRow5}>
          {(
            [
              ["Total Orders", dashboardMetrics?.statCards.totalOrders],
              ["Total Returns", dashboardMetrics?.statCards.totalReturns],
              ["Canceled", dashboardMetrics?.statCards.canceled],
              ["Delivered", dashboardMetrics?.statCards.delivered],
              ["Rescheduled", dashboardMetrics?.statCards.rescheduled]
            ] as Array<[string, StatCardData | undefined]>
          ).map(([label, card]) => (
            <InlineCellShell key={label}>
              <Stat
                size="md"
                title={label}
                amount={formatCount(card?.total ?? 0)}
              />
              <div className={styles.statCard__today}>
                <span className={styles.statCard__todayLabel}>
                  {t("Today")}
                </span>
                <div className={styles.statCard__todayRow}>
                  <span className={styles.statCard__todayValue}>
                    {formatToday(card?.today ?? 0)}
                  </span>
                  <TrendBadge value={card?.trendPct ?? 0} />
                </div>
              </div>
            </InlineCellShell>
          ))}
        </div>
      </div>

      {/* Critical Delivery KPIs — single card with 4 inline cells */}
      <div className={styles.card}>
        <h3 className={styles.sectionTitle}>{t("Critical Delivery KPIs")}</h3>
        <div className={styles.inlineRow + " " + styles.inlineRow4}>
          <InlineCellShell>
            <div className={styles.kpiCard__head}>
              <KpiCheckIcon width={20} height={20} />
              <span className={styles.kpiCard__label}>
                {t("Successful delivery rate")}
              </span>
            </div>
            <div className={styles.kpiCard__value}>
              {(dashboardMetrics?.kpis.successRate.value ?? 0).toFixed(1)}%
            </div>
            <TrendBadge
              value={dashboardMetrics?.kpis.successRate.trendPct ?? 0}
              label={trendLabel}
            />
          </InlineCellShell>

          <InlineCellShell>
            <div className={styles.kpiCard__head}>
              <KpiClockIcon width={20} height={20} />
              <span className={styles.kpiCard__label}>
                {t("On-time delivery")}
              </span>
            </div>
            <div className={styles.kpiCard__value}>
              {(dashboardMetrics?.kpis.onTimeDelivery.value ?? 0).toFixed(1)}%
            </div>
            <TrendBadge
              value={dashboardMetrics?.kpis.onTimeDelivery.trendPct ?? 0}
              label={trendLabel}
            />
          </InlineCellShell>

          <InlineCellShell>
            <div className={styles.kpiCard__head}>
              <KpiCrossIcon width={20} height={20} />
              <span className={styles.kpiCard__label}>
                {t("Failed delivery rate")}
              </span>
            </div>
            <div className={styles.kpiCard__value}>
              {(dashboardMetrics?.kpis.failedRate.value ?? 0).toFixed(1)}%
            </div>
            <div className={styles.kpiCard__sub}>
              {dashboardMetrics?.kpis.failedRate.failedCount ?? 0} {t("of")}{" "}
              {(
                dashboardMetrics?.kpis.failedRate.totalCount ?? 0
              ).toLocaleString("en-US")}{" "}
              {t("orders")}
            </div>
            <TrendBadge
              value={dashboardMetrics?.kpis.failedRate.trendPct ?? 0}
              label={trendLabel}
            />
          </InlineCellShell>

          <InlineCellShell>
            <div className={styles.kpiCard__head}>
              <KpiStarIcon width={20} height={20} />
              <span className={styles.kpiCard__label}>
                {t("Avg customer rating")}
              </span>
            </div>
            <div className={styles.kpiCard__value}>
              {(dashboardMetrics?.kpis.avgRating.value ?? 0).toFixed(2)}
              <span className={styles.kpiCard__suffix}> / 5</span>
            </div>
            <div className={styles.kpiCard__sub}>
              {t("Based on")}{" "}
              {(
                dashboardMetrics?.kpis.avgRating.ratingsCount ?? 0
              ).toLocaleString("en-US")}{" "}
              {t("ratings")}
            </div>
            <TrendBadge
              value={dashboardMetrics?.kpis.avgRating.trendPct ?? 0}
              label={trendLabel}
            />
          </InlineCellShell>
        </div>
      </div>

      {/* Real-Time Operations + Financial Overview — single card 4 cells, two titles */}
      <div className={styles.card}>
        <div className={styles.dualHeader}>
          <h3 className={styles.sectionTitle}>{t("Real-Time Operations")}</h3>
          <h3 className={styles.sectionTitle}>{t("Financial Overview")}</h3>
        </div>
        <div className={styles.inlineRow + " " + styles.inlineRow4}>
          <InlineCellShell>
            <div className={styles.kpiCard__head}>
              <TruckIcon width={20} height={20} />
              <span className={styles.kpiCard__label}>{t("In transit")}</span>
            </div>
            <div className={styles.kpiCard__value}>
              {dashboardMetrics?.realTime.inTransit ?? 0}
            </div>
            <div className={styles.kpiCard__live}>
              <span className={styles.liveDot} /> {t("Live")} ·{" "}
              {dashboardMetrics?.realTime.activeDriversCount ?? 0}{" "}
              {t("drivers active")}
            </div>
          </InlineCellShell>

          <InlineCellShell>
            <div className={styles.kpiCard__head}>
              <HourglassIcon width={20} height={20} />
              <span className={styles.kpiCard__label}>
                {t("Pending assignment")}
              </span>
            </div>
            <div className={styles.kpiCard__value}>
              {dashboardMetrics?.realTime.pendingAssignment ?? 0}
            </div>
          </InlineCellShell>

          <InlineCellShell>
            <span className={styles.kpiCard__label}>
              {t("Money collected")}
            </span>
            <div className={styles.kpiCard__value}>
              {formatCount(dashboardMetrics?.financial.moneyCollected ?? 0)}
            </div>
            <div className={styles.kpiCard__sub}>
              FCFA · {t("cash on delivery")}
            </div>
          </InlineCellShell>

          <InlineCellShell>
            <span className={styles.kpiCard__label}>{t("Turnover")}</span>
            <div className={styles.kpiCard__value}>
              {formatCount(dashboardMetrics?.financial.turnover ?? 0)}
            </div>
            <div className={styles.kpiCard__sub}>
              FCFA · {t("paid by the customers")}
            </div>
          </InlineCellShell>
        </div>
      </div>

      {/* Driver Performance + Leaderboard — unified card */}
      <div className={styles.dpCard}>
        <div className={styles.dpHeader}>
          <h3>{t("Driver Performance")}</h3>
        </div>

        <div className={styles.dpStats}>
          <div className={styles.dpStatCell}>
            <div className={styles.dpStatCell__label}>
              {t("Active drivers")}
            </div>
            <div className={styles.dpStatCell__value}>
              {dpStats?.activeDrivers ?? 0}
              <span className={styles.dpStatCell__suffix}>
                {" "}
                / {dpStats?.totalDrivers ?? 0}
              </span>
            </div>
          </div>
          <div className={styles.dpStatCell}>
            <div className={styles.dpStatCell__label}>
              {t("Avg deliveries")}
            </div>
            <div className={styles.dpStatCell__value}>
              {fmtNumber(dpStats?.avgDeliveriesPerDay ?? 0)}
              <span className={styles.dpStatCell__suffix}> / {t("day")}</span>
            </div>
          </div>
          <div className={styles.dpStatCell}>
            <div className={styles.dpStatCell__label}>
              {t("Avg success rate / driver")}
            </div>
            <div className={styles.dpStatCell__value}>
              {fmtNumber(dpStats?.avgSuccessRate ?? 0)}%
            </div>
          </div>
          <div className={styles.dpStatCell}>
            <div className={styles.dpStatCell__label}>
              {t("Avg failure rate / driver")}
            </div>
            <div className={styles.dpStatCell__value}>
              {fmtNumber(dpStats?.avgFailureRate ?? 0)}%
            </div>
          </div>
        </div>

        <div className={styles.lbHead}>
          <h3>{t("Driver Leaderboard")}</h3>
          <div className={styles.lbTabs}>
            <button
              className={
                styles.lbTab +
                (leaderboardTab === "top" ? " " + styles.lbTabActive : "")
              }
              onClick={() => setLeaderboardTab("top")}
              type="button"
            >
              {t("Top performers")}
              <span
                className={
                  styles.lbTabBadge +
                  (leaderboardTab === "top"
                    ? " " + styles.lbTabBadgeActive
                    : "")
                }
              >
                {topCount}
              </span>
            </button>
            <button
              className={
                styles.lbTab +
                (leaderboardTab === "under" ? " " + styles.lbTabActive : "")
              }
              onClick={() => setLeaderboardTab("under")}
              type="button"
            >
              {t("Underperforming")}
              <span
                className={
                  styles.lbTabBadge +
                  (leaderboardTab === "under"
                    ? " " + styles.lbTabBadgeActive
                    : "")
                }
              >
                {underCount}
              </span>
            </button>
            <button
              className={
                styles.lbTab +
                (leaderboardTab === "all" ? " " + styles.lbTabActive : "")
              }
              onClick={() => setLeaderboardTab("all")}
              type="button"
            >
              {t("All drivers")}
              <span
                className={
                  styles.lbTabBadge +
                  (leaderboardTab === "all" ? " " + styles.lbTabBadgeActive : "")
                }
              >
                {allCount}
              </span>
            </button>
          </div>
        </div>

        <div className={styles.lbTable}>
          <div className={styles.lbHeaderRow}>
            <div
              className={
                styles.lbHeaderRow__col + " " + styles.lbHeaderRow__driver
              }
            >
              {t("Driver")}
            </div>
            <div
              className={
                styles.lbHeaderRow__col + " " + styles.lbHeaderRow__status
              }
            >
              {t("Status")}
            </div>
            <div
              className={
                styles.lbHeaderRow__col + " " + styles.lbHeaderRow__flex
              }
            >
              {t("Orders")}
            </div>
            <div
              className={
                styles.lbHeaderRow__col + " " + styles.lbHeaderRow__flex
              }
            >
              {t("Success Rate")}
            </div>
            <div
              className={
                styles.lbHeaderRow__col + " " + styles.lbHeaderRow__flex
              }
            >
              {t("Failed Rate")}
            </div>
            <div className={styles.lbHeaderRow__menu} />
          </div>

          {leaderboardRows.length === 0 ? (
            <div className={styles.emptyHint}>
              {t("No drivers in this category yet.")}
            </div>
          ) : (
            leaderboardRows.map((d) => (
              <div key={d.id} className={styles.lbRow}>
                <div className={styles.lbRow__driver}>
                  <div className={styles.lbRow__avatar}>
                    {d.imageUrl ? (
                      <img src={d.imageUrl} alt={d.name} />
                    ) : (
                      <AvatarPlaceholder width={32} height={32} />
                    )}
                  </div>
                  <span className={styles.lbRow__name}>{d.name}</span>
                </div>
                <div className={styles.lbRow__statusCol}>
                  <span
                    className={
                      styles.lbStatus + " " + styles[STATUS_CLASS[d.statusKey]]
                    }
                  >
                    {t(STATUS_LABEL[d.statusKey])}
                  </span>
                </div>
                <div className={styles.lbRow__cell}>{d.orders}</div>
                <div className={styles.lbRow__cell}>
                  <div className={styles.lbRow__barRow}>
                    <div className={styles.lbBar}>
                      <div
                        className={
                          styles.lbBarFill + " " + styles.lbBarFillGreen
                        }
                        style={{ width: `${d.successRate}%` }}
                      />
                    </div>
                    <span className={styles.lbPct}>{d.successRate}%</span>
                  </div>
                </div>
                <div className={styles.lbRow__cell}>
                  <div className={styles.lbRow__barRow}>
                    <div className={styles.lbBar}>
                      <div
                        className={styles.lbBarFill + " " + styles.lbBarFillRed}
                        style={{ width: `${d.failedRate}%` }}
                      />
                    </div>
                    <span className={styles.lbPct}>{d.failedRate}%</span>
                  </div>
                </div>
                <button
                  className={styles.lbRow__menuBtn}
                  type="button"
                  aria-label={t("Actions")}
                >
                  <VerticalDotsIcon />
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Contact Details & SLA — unified card with all 4 sections */}
      {showContactDetails && (
        <div className={styles.cdCard}>
          <div className={styles.cdHeader}>
            <h3>{t("Contact Details & SLA")}</h3>
          </div>

          <div className={styles.cdRow}>
            <div className={styles.cdCell}>
              <div className={styles.slaHead}>
                <div>
                  <div className={styles.slaTitle}>
                    {sla
                      ? `${sla.title}${sla.version ? ` · ${sla.version}` : ""}`
                      : t("No agreement set")}
                  </div>
                  {sla?.counterparty || sla?.contractNumber ? (
                    <div className={styles.slaSubtitle}>
                      {sla?.counterparty
                        ? `${t("With")} ${sla.counterparty}`
                        : ""}
                      {sla?.counterparty && sla?.contractNumber ? " · " : ""}
                      {sla?.contractNumber
                        ? `${t("contract")} #${sla.contractNumber}`
                        : ""}
                    </div>
                  ) : null}
                </div>
                <div className={styles.slaTitleRow}>
                  {sla && (
                    <span
                      className={
                        styles.slaBadge +
                        " " +
                        styles[SLA_STATUS_CLASS[sla.status]]
                      }
                    >
                      {t(SLA_STATUS_LABEL[sla.status])}
                    </span>
                  )}
                  <button
                    type="button"
                    className={styles.slaEdit}
                    onClick={() => setIsSlaOpen(true)}
                  >
                    <EditIcon />
                    {sla ? t("Edit") : t("Add")}
                  </button>
                </div>
              </div>
              <div className={styles.slaGrid}>
                <div>
                  <div className={styles.slaLabel}>{t("Effective Period")}</div>
                  <div className={styles.slaValue}>
                    {formatRange(
                      sla?.effectiveFrom ?? null,
                      sla?.effectiveTo ?? null
                    )}
                  </div>
                </div>
                <div>
                  <div className={styles.slaLabel}>
                    {t("Guaranteed delivery")}
                  </div>
                  <div className={styles.slaValue}>
                    {sla?.guaranteedDelivery || "—"}
                  </div>
                </div>
                <div>
                  <div className={styles.slaLabel}>{t("in success rate")}</div>
                  <div className={styles.slaValue}>
                    {sla?.successRate != null ? `${sla.successRate}%` : "—"}
                  </div>
                </div>
                <div>
                  <div className={styles.slaLabel}>
                    {t("Penalty per breach")}
                  </div>
                  <div className={styles.slaValue}>
                    {sla?.penaltyPerBreach != null
                      ? `${sla.penaltyPerBreach.toLocaleString("en-US")} FCFA / ${t("order")}`
                      : "—"}
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.cdCell}>
              <div className={styles.contactHead}>
                <div className={styles.contactHead__title}>
                  {t("Operational contact")}
                </div>
                <button
                  className={styles.contactHead__edit}
                  onClick={() => setIsContactOpen(true)}
                  type="button"
                >
                  <EditIcon />
                  {contact ? t("Edit") : t("Add")}
                </button>
              </div>
              {contact ? (
                <div className={styles.contactBlock}>
                  <div className={styles.contactPerson}>
                    <div className={styles.contactPersonAvatar}>
                      <Avatar
                        image={
                          contact.avatar ? { url: contact.avatar.url } : null
                        }
                        dimensions={{ width: 32, height: 32 }}
                      />
                    </div>
                    <div>
                      <div className={styles.contactName}>
                        {contact.fullName}
                      </div>
                      {contact.role && (
                        <div className={styles.contactRole}>{contact.role}</div>
                      )}
                    </div>
                  </div>
                  <div className={styles.contactRows}>
                    {contact.phoneNumber && (
                      <div className={styles.contactRow}>
                        <span className={styles.contactRow__icon}>
                          <PhoneIcon />
                        </span>
                        <span className={styles.contactRow__label}>
                          {t("Phone number")}
                        </span>
                        <span className={styles.contactRow__value}>
                          {contact.phoneNumber}
                        </span>
                      </div>
                    )}
                    {contact.phoneNumber && contact.email && (
                      <div className={styles.contactRowDivider} />
                    )}
                    {contact.email && (
                      <div className={styles.contactRow}>
                        <span className={styles.contactRow__icon}>
                          <UserIcon />
                        </span>
                        <span className={styles.contactRow__label}>
                          {t("Email")}
                        </span>
                        <span className={styles.contactRow__value}>
                          {contact.email}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className={styles.emptyHint}>
                  {t("No operational contact yet.")}
                </div>
              )}
            </div>
          </div>

          {/* Sub-headers row: Recent communication / Legal documents */}
          <div className={styles.cdSubRow}>
            <div className={styles.cdSubHead}>
              <h4>{t("Recent communication")}</h4>
              <button
                className={styles.linkBtn}
                onClick={() => {
                  setEditingLog(null);
                  setIsLogOpen(true);
                }}
                type="button"
              >
                <PlusIcon />
                {t("Add entry")}
              </button>
            </div>
            <div className={styles.cdSubHead}>
              <h4>{t("Legal documents")}</h4>
              <button
                className={styles.linkBtn}
                onClick={() => {
                  setEditingDoc(null);
                  setIsDocOpen(true);
                }}
                type="button"
              >
                <PlusIcon />
                {t("Upload")}
              </button>
            </div>
          </div>

          {/* Items row */}
          <div className={styles.cdRow}>
            <div className={styles.cdItemsCol}>
              {logs.length === 0 ? (
                <div className={styles.emptyHint}>{t("No entries yet.")}</div>
              ) : (
                <ul className={styles.commList}>
                  {logs.map((log, idx) => {
                    const isLast = idx === logs.length - 1;
                    return (
                      <li key={log.id} className={styles.commItem}>
                        <div className={styles.commItem__pinCol}>
                          {!isLast && (
                            <div className={styles.commItem__connector} />
                          )}
                          <div
                            className={
                              isLast
                                ? styles.commItem__pinCurrent
                                : styles.commItem__pin
                            }
                          />
                        </div>
                        <div className={styles.commItem__content}>
                          <div className={styles.commItem__title}>
                            {log.title}
                          </div>
                          <div className={styles.commItem__date}>
                            {moment(log.date).format("dddd, MMM D")}
                          </div>
                        </div>
                        <TableItemActionsControl
                          rowIndex={idx}
                          openedItemOptionsIndex={openedLogIdx}
                          setOpenedItemOptionsIndex={setOpenedLogIdx}
                          actionsList={[
                            {
                              Icon: <EditIcon width={14} height={14} />,
                              label: "Edit",
                              onClick: () => {
                                setEditingLog(log);
                                setIsLogOpen(true);
                                setOpenedLogIdx(null);
                              }
                            },
                            {
                              Icon: (
                                <TrashAltIcon
                                  width={14}
                                  height={14}
                                  fill="#FF0033"
                                />
                              ),
                              label: "Delete",
                              labelColor: "#FF0033",
                              onClick: () => {
                                deleteLog(log.id);
                                setOpenedLogIdx(null);
                              }
                            }
                          ]}
                        />
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className={styles.cdItemsCol}>
              {docs.length === 0 ? (
                <div className={styles.emptyHint}>{t("No documents yet.")}</div>
              ) : (
                <ul className={styles.docList}>
                  {docs.map((d, idx) => {
                    const expiry = formatExpiry(d.expiresAt);
                    return (
                      <li key={d.id} className={styles.docItem}>
                        <div className={styles.docItem__main}>
                          <span className={styles.docIcon}>
                            <DocumentDownloadIcon />
                          </span>
                          <span className={styles.docItem__name}>{d.name}</span>
                        </div>
                        <div className={styles.docItem__meta}>
                          <div className={styles.docItem__metaLabel}>
                            {t(expiry.sub)}
                          </div>
                          <div
                            className={
                              styles.docItem__metaValue +
                              (expiry.warn
                                ? " " + styles.docItem__metaValueWarn
                                : "")
                            }
                          >
                            {expiry.label}
                          </div>
                        </div>
                        <div className={styles.docItem__statusCol}>
                          <span className={styles.docItem__status}>
                            {t("Verified")}
                          </span>
                        </div>
                        <TableItemActionsControl
                          rowIndex={idx}
                          openedItemOptionsIndex={openedDocIdx}
                          setOpenedItemOptionsIndex={setOpenedDocIdx}
                          actionsList={[
                            {
                              Icon: <EditIcon width={14} height={14} />,
                              label: "Edit",
                              onClick: () => {
                                setEditingDoc(d);
                                setIsDocOpen(true);
                                setOpenedDocIdx(null);
                              }
                            },
                            {
                              Icon: (
                                <TrashAltIcon
                                  width={14}
                                  height={14}
                                  fill="#FF0033"
                                />
                              ),
                              label: "Delete",
                              labelColor: "#FF0033",
                              onClick: () => {
                                deleteDoc(d.id);
                                setOpenedDocIdx(null);
                              }
                            }
                          ]}
                        />
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {showContactDetails && (
        <>
          <CompanySlaModal
            isOpen={isSlaOpen}
            companyId={companyId}
            sla={sla}
            onClose={() => setIsSlaOpen(false)}
          />
          <OperationalContactModal
            isOpen={isContactOpen}
            companyId={companyId}
            contact={contact}
            onClose={() => setIsContactOpen(false)}
          />
        </>
      )}
      <CommunicationLogModal
        isOpen={isLogOpen}
        companyId={companyId}
        log={editingLog}
        onClose={() => {
          setIsLogOpen(false);
          setEditingLog(null);
        }}
      />
      <LegalDocumentModal
        isOpen={isDocOpen}
        companyId={companyId}
        document={editingDoc}
        onClose={() => {
          setIsDocOpen(false);
          setEditingDoc(null);
        }}
      />
    </div>
  );
};

export default CompanyMetricsTab;
